import React, { useEffect, useState, useCallback } from "react";
import { auleloApi } from "@/lib/auleloApi";
import { Button } from "@/components/ui/button";
import { Loader2, Wand2, Mic, Play, Pause, Brain, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AuleloBriefing() {
  const [digests, setDigests] = useState([]);
  const [latest, setLatest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [audio, setAudio] = useState(null); // {url, digestId, playing, voice}
  const [playing, setPlaying] = useState(false);
  const audioRef = React.useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await auleloApi.get("/digest");
      setDigests(data || []);
      if ((data || []).length) setLatest(data[0]);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runDigest = async () => {
    setBusy(true);
    try {
      const { data } = await auleloApi.post("/digest");
      setLatest(data);
      await load();
      toast.success("Brief compiled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest failed");
    } finally {
      setBusy(false);
    }
  };

  const playPodcast = async () => {
    if (!latest) {
      toast.message("Generate a brief first");
      return;
    }
    setBusy(true);
    try {
      const { data } = await auleloApi.post("/podcast", { digest_id: latest.id });
      // Build a data URL audio src
      const url = `data:audio/mpeg;base64,${data.audio_base64}`;
      setAudio({ url, digestId: data.digest_id, voice: data.voice });
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.play();
          setPlaying(true);
        }
      }, 100);
      toast.success(`Podcast ready · voice: ${data.voice}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "TTS failed");
    } finally {
      setBusy(false);
    }
  };

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play(); setPlaying(true); }
  };

  return (
    <div className="space-y-8" data-testid="aulelo-briefing">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-400">// daily brief</div>
          <h1 className="font-mono text-4xl md:text-5xl font-black tracking-tight mt-2 text-emerald-50">Today, on three fronts.</h1>
          <p className="text-zinc-400 mt-2 font-mono text-sm">India ▸ World ▸ China. Compiled by GPT-5.2 + Gemini 3 Pro. No editorialising.</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={runDigest}
            disabled={busy}
            data-testid="aulelo-compile-btn"
            className="rounded-none bg-emerald-500 hover:bg-emerald-400 text-black font-mono uppercase tracking-[0.2em] text-xs h-11 px-5"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Compile brief
          </Button>
          <Button
            onClick={playPodcast}
            disabled={busy || !latest}
            data-testid="aulelo-podcast-btn"
            variant="outline"
            className="rounded-none border-emerald-700/40 bg-transparent text-emerald-200 hover:bg-emerald-500/10 font-mono uppercase tracking-[0.2em] text-xs h-11 px-5"
          >
            <Mic className="w-4 h-4 mr-2" />
            Render podcast
          </Button>
        </div>
      </div>

      {audio && (
        <div className="border border-emerald-700/40 bg-black p-4 flex items-center gap-4" data-testid="aulelo-audio-bar">
          <button
            onClick={toggle}
            data-testid="aulelo-audio-toggle"
            className="w-10 h-10 border border-emerald-500 bg-emerald-500/10 text-emerald-300 flex items-center justify-center hover:bg-emerald-500/20"
          >
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <div className="flex-1">
            <div className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-300">Morning podcast</div>
            <div className="text-zinc-500 text-[10px] font-mono mt-0.5">voice: {audio.voice} · OpenAI TTS HD</div>
          </div>
          <audio
            ref={audioRef}
            src={audio.url}
            onEnded={() => setPlaying(false)}
            data-testid="aulelo-audio-el"
          />
        </div>
      )}

      {latest ? (
        <div className="grid lg:grid-cols-3 gap-px bg-emerald-900/30 border border-emerald-900/40" data-testid="aulelo-brief-content">
          <Section icon={Sparkles} title="India" body={latest.india} subtitle="grounded · Gemini 3 Pro" />
          <Section icon={Brain} title="World" body={latest.international} subtitle="analysis · GPT-5.2" />
          <Section icon={Brain} title="China" body={latest.china} subtitle="analysis · GPT-5.2" />
        </div>
      ) : (
        <div className="border border-dashed border-emerald-900/40 p-10 text-center text-zinc-500 font-mono text-sm">
          No brief yet. Hit <b className="text-emerald-300">Compile brief</b> above to fetch fresh news and have the agents read it for you.
        </div>
      )}

      {digests.length > 1 && (
        <div className="mt-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">// archive</div>
          <div className="space-y-2">
            {digests.slice(1, 6).map((d) => (
              <button
                key={d.id}
                onClick={() => setLatest(d)}
                data-testid={`aulelo-archive-${d.id}`}
                className="w-full text-left border border-zinc-800 bg-black p-3 hover:border-emerald-700/40 transition"
              >
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{String(d.created_at).slice(0, 16).replace("T", " · ")}</div>
                <div className="text-zinc-300 text-sm mt-1 line-clamp-2 font-mono">{d.india?.slice(0, 200)}…</div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Section = ({ icon: Icon, title, body, subtitle }) => {
  const bullets = React.useMemo(() => {
    const text = (body || "").trim();
    if (!text) return null;
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const looksBullety = lines.filter((l) => /^[\u2022\-\*]/.test(l)).length >= 2;
    if (looksBullety) return lines.map((l) => l.replace(/^[\u2022\-\*]+\s*/, ""));
    return null;
  }, [body]);
  return (
    <div className="bg-black p-6" data-testid={`aulelo-section-${title.toLowerCase()}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5 text-emerald-400" />
        <div className="font-mono uppercase tracking-[0.25em] text-[11px] text-emerald-300">{title}</div>
      </div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-600 mt-0.5">{subtitle}</div>
      {bullets ? (
        <ul className="mt-4 space-y-2.5">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-3 text-zinc-200 text-[14px] leading-relaxed font-mono">
              <span className="text-emerald-500 shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="flex-1">{b}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-4 text-zinc-200 leading-relaxed text-[15px] whitespace-pre-wrap font-mono">{body || "—"}</p>
      )}
    </div>
  );
};
